import type React from "react";
import { cn } from "@/lib/utils";

export interface CompareRowSpec {
  label: string;
  format: (v: number) => string;
  direction: "higher" | "lower" | "neutral";
}

export interface CompareSide {
  name: string;
  value: number;
}

export function CompareRow({
  spec,
  a,
  b,
  extra,
  colorClassA = "bg-phoenix-orange",
  colorClassB = "bg-eagle-blue",
}: {
  spec: CompareRowSpec;
  a: CompareSide;
  b: CompareSide;
  extra?: string | null;
  colorClassA?: string;
  colorClassB?: string;
}): React.JSX.Element {
  const max = Math.max(a.value, b.value, Number.EPSILON);
  const aPct = (a.value / max) * 100;
  const bPct = (b.value / max) * 100;

  let winner: "a" | "b" | "tie" = "tie";
  if (spec.direction !== "neutral" && a.value !== b.value) {
    const higher = a.value > b.value ? "a" : "b";
    const lower = a.value > b.value ? "b" : "a";
    winner = spec.direction === "higher" ? higher : lower;
  }

  const delta = Math.abs(a.value - b.value);
  const showDelta = winner !== "tie" && delta > 0;

  return (
    <div className="grid grid-cols-1 gap-3 border-b border-border px-5 py-4 last:border-b-0 md:grid-cols-[180px_1fr_180px]">
      <div className="text-sm">
        <p className="font-medium text-foreground">{spec.label}</p>
        <p className="text-xs text-muted-foreground">
          {spec.direction === "higher"
            ? "Higher is better"
            : spec.direction === "lower"
              ? "Lower is better"
              : "Neutral"}
        </p>
        {extra ? <p className="text-xs text-muted-foreground mt-1">{extra}</p> : null}
      </div>

      <div className="space-y-2">
        <CompareBar
          name={a.name}
          value={a.value}
          pct={aPct}
          colorClass={colorClassA}
          dim={winner === "b"}
          format={spec.format}
        />
        <CompareBar
          name={b.name}
          value={b.value}
          pct={bPct}
          colorClass={colorClassB}
          dim={winner === "a"}
          format={spec.format}
        />
      </div>

      <div className="flex items-center md:justify-end">
        {showDelta ? (
          <div className="text-right">
            <p className="num text-xs text-muted-foreground tabular-nums">
              Δ {spec.format(delta)}
            </p>
            <p className="text-xs font-medium text-foreground">
              {winner === "a" ? a.name : b.name} leads
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function CompareBar({
  name,
  value,
  pct,
  colorClass,
  dim,
  format,
}: {
  name: string;
  value: number;
  pct: number;
  colorClass: string;
  dim: boolean;
  format: (v: number) => string;
}): React.JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <span className="w-24 truncate text-xs text-muted-foreground">{name}</span>
      <div className="relative h-3 flex-1 rounded-full bg-muted">
        <div
          className={cn(
            "absolute inset-y-0 left-0 rounded-full transition-all",
            colorClass,
            dim ? "opacity-55" : "opacity-100",
          )}
          style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
        />
      </div>
      <span className="num w-20 text-right text-xs font-semibold tabular-nums">
        {format(value)}
      </span>
    </div>
  );
}
